import { useMemo, useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Badge } from './ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiContentSave, mdiFlaskOutline, mdiLoading, mdiPound, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { $api } from '../api/queryClient'
import { useAssetTypes } from '../api/queries'
import { useCan } from '../contexts/PermissionsContext'
import { AggregationTestPanel } from './aggregations/AggregationTestPanel'
import { AggregationConditionEditor, isConditionComplete, type AggregationCondition } from './aggregations/AggregationConditionEditor'
import type { AggregationDetail, UpdateAggregationRequest } from '../api/schema'
import { AssetTypeTile, type AssetTypeLike } from './assets/AssetTypeTile'

type AggregationFunctionCode = NonNullable<UpdateAggregationRequest['function']>

/** Narrows an already-parsed `filter_conditions`/`ratio_denominator_conditions`
 * response value down to the structured leaf shape, or null if it doesn't
 * match (e.g. legacy/malformed data stored before this editor existed). */
function toCondition(raw: unknown): AggregationCondition | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  if (typeof obj.property !== 'string' || typeof obj.op !== 'string') return null
  return {
    property: obj.property,
    op: obj.op as AggregationCondition['op'],
    value: obj.value === undefined || obj.value === null ? '' : String(obj.value),
  }
}

const FUNCTIONS: Array<{ value: AggregationFunctionCode; needsSource: boolean; needsFilter: boolean }> = [
  { value: 'count', needsSource: false, needsFilter: false },
  { value: 'count_where', needsSource: false, needsFilter: true },
  { value: 'count_where_observed', needsSource: false, needsFilter: true },
  { value: 'sum', needsSource: true, needsFilter: false },
  { value: 'avg', needsSource: true, needsFilter: false },
  { value: 'min', needsSource: true, needsFilter: false },
  { value: 'max', needsSource: true, needsFilter: false },
  { value: 'ratio', needsSource: false, needsFilter: true },
]

function invalidateAggregation(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1] as string
        return path.includes('/aggregations')
      }
      return false
    }
  })
}

export function AggregationDetailPage() {
  const { org, uid } = useParams({ strict: false }) as { org: string; uid: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('aggregations')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')

  // Editable fields
  const [code, setCode] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [description, setDescription] = useState('')
  const [fn, setFn] = useState<AggregationFunctionCode | ''>('')
  const [targetAssetTypeCode, setTargetAssetTypeCode] = useState('')
  const [targetPropertyCode, setTargetPropertyCode] = useState('')
  const [childAssetTypeCode, setChildAssetTypeCode] = useState('')
  const [sourcePropertyCode, setSourcePropertyCode] = useState('')
  const [filterCondition, setFilterCondition] = useState<AggregationCondition | null>(null)
  const [ratioDenominatorCondition, setRatioDenominatorCondition] = useState<AggregationCondition | null>(null)
  const [enabled, setEnabled] = useState(true)

  const { data: assetTypes = [] } = useAssetTypes(org)
  // Aggregations belong to the type system: `configuration.write` gates every
  // mutation, while the definition itself stays readable (spec 2026-09-04-02).
  const canWriteConfiguration = useCan('configuration.write')

  const selectedFn = FUNCTIONS.find(f => f.value === fn)

  useBreadcrumbs(useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb'), path: `/${org}/aggregations` },
    { title: code || t('common:status.loading') }
  ], [org, code, t]))

  const { data: aggregation, isLoading, error } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/aggregations/{aggregationUid}', {
    params: { path: { orgCode: org, aggregationUid: uid } }
  })

  const agg = aggregation as AggregationDetail | undefined

  useEffect(() => {
    if (agg) {
      setCode(agg.code || '')
      setNameEn(agg.name?.en || '')
      setNameFr(agg.name?.fr || '')
      setDescription(agg.description || '')
      setFn(agg.function || '')
      setTargetAssetTypeCode(agg.target_asset_type?.code || '')
      setTargetPropertyCode(agg.target_property?.code || '')
      setChildAssetTypeCode(agg.child_asset_type_code || '')
      setSourcePropertyCode(agg.source_property_code || '')
      setFilterCondition(toCondition(agg.filter_conditions))
      setRatioDenominatorCondition(toCondition(agg.ratio_denominator_conditions))
      setEnabled(agg.enabled ?? true)
    }
  }, [agg])

  const updateMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/aggregations/{aggregationUid}', {
    onSuccess: () => invalidateAggregation(queryClient)
  })

  const deleteMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/aggregations/{aggregationUid}', {
    onSuccess: () => {
      invalidateAggregation(queryClient)
      navigate({ to: '/$org/aggregations', params: { org } })
    }
  })

  const filterConditionValid = !selectedFn?.needsFilter || isConditionComplete(filterCondition)
  const ratioConditionValid = fn !== 'ratio' || isConditionComplete(ratioDenominatorCondition)
  const formValid = filterConditionValid && ratioConditionValid

  const handleSave = useCallback(() => {
    if (!formValid) return

    updateMutation.mutate({
      params: { path: { orgCode: org, aggregationUid: uid } },
      body: {
        code,
        name: { en: nameEn, fr: nameFr },
        description: description || undefined,
        function: fn as AggregationFunctionCode,
        target_asset_type_code: targetAssetTypeCode,
        target_property_code: targetPropertyCode,
        child_asset_type_code: childAssetTypeCode,
        source_property_code: selectedFn?.needsSource ? sourcePropertyCode : undefined,
        // `filter_conditions`/`ratio_denominator_conditions` are typed as an
        // empty `object` schema (no properties) since the backend accepts
        // any leaf-condition shape — this is the one legitimate remaining
        // cast, narrowed to just these two fields.
        filter_conditions: (filterCondition ?? null) as unknown as Record<string, never> | null,
        ratio_denominator_conditions: (ratioDenominatorCondition ?? null) as unknown as Record<string, never> | null,
        enabled,
      }
    })
  }, [formValid, code, nameEn, nameFr, description, fn, targetAssetTypeCode, targetPropertyCode, childAssetTypeCode, sourcePropertyCode, filterCondition, ratioDenominatorCondition, enabled, org, uid, updateMutation, selectedFn])

  const handleToggleEnabled = useCallback((checked: boolean) => {
    setEnabled(checked)
    updateMutation.mutate({
      params: { path: { orgCode: org, aggregationUid: uid } },
      body: { enabled: checked }
    })
  }, [org, uid, updateMutation])

  const handleDelete = useCallback(() => {
    deleteMutation.mutate({
      params: { path: { orgCode: org, aggregationUid: uid } }
    })
  }, [org, uid, deleteMutation])

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
      </div>
    )
  }

  if (error || !agg) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-destructive text-center py-8">
          {t('not_found')}
        </div>
      </div>
    )
  }

  const targetAssetType = assetTypes.find((at: any) => at.code === targetAssetTypeCode) as any
  const childAssetType = assetTypes.find((at: any) => at.code === childAssetTypeCode) as any

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{agg.name?.en || agg.code}</h1>
          <code className="text-sm text-muted-foreground">{agg.code}</code>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {/* The toggle doubles as the enabled indicator: it stays on screen,
                read-only, rather than disappearing. */}
            <Checkbox
              checked={enabled}
              disabled={!canWriteConfiguration}
              onCheckedChange={(checked) => handleToggleEnabled(checked === true)}
              data-testid="aggregation-enabled-toggle"
            />
            <Label className="text-sm">{t('detail.enabled_label')}</Label>
          </div>
          {canWriteConfiguration && (
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-aggregation-btn"
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          )}
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-2 mb-6 ml-10">
        <Badge variant="secondary">{t(`functions.${agg.function}`, { defaultValue: agg.function })}</Badge>
        <Badge variant="secondary" className="gap-1.5">
          Target: <code className="text-xs">{agg.target_property?.code || targetPropertyCode}</code>
        </Badge>
        {targetAssetType && (
          <Badge variant="secondary" className="gap-1.5">
            <AssetTypeTile type={targetAssetType as AssetTypeLike} size="sm" />
            {typeof targetAssetType.name === 'string' ? targetAssetType.name : targetAssetType.name?.en || targetAssetType.code}
          </Badge>
        )}
        {childAssetType && (
          <Badge variant="secondary" className="gap-1.5">
            &rarr; <AssetTypeTile type={childAssetType as AssetTypeLike} size="sm" />
            {typeof childAssetType.name === 'string' ? childAssetType.name : childAssetType.name?.en || childAssetType.code}
          </Badge>
        )}
        {agg.source_property_code && (
          <Badge variant="secondary" className="gap-1.5">
            Source: <code className="text-xs">{agg.source_property_code}</code>
          </Badge>
        )}
        {/* An aggregation may read an asset column instead of a property —
            the fleet-age cards aggregate `assets.start_date` since the
            commissioning date moved onto the column (spec 2026-09-03-02). */}
        {agg.source_asset_column && (
          <Badge variant="secondary" className="gap-1.5">
            Source: <code className="text-xs">assets.{agg.source_asset_column}</code>
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview" className="gap-1.5">
            <AppIcon path={mdiPound} size="xs" />
            {t('detail.tabs.overview')}
          </TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test" className="gap-1.5">
            <AppIcon path={mdiFlaskOutline} size="xs" />
            {t('detail.tabs.test')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Identity */}
            <div className="bg-card rounded-lg border border-border p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{t('detail.identity_section')}</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-code">{t('detail.code_label')}</Label>
                  <Input
                    id="edit-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="font-mono"
                    readOnly={!canWriteConfiguration}
                    data-testid="edit-code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name-en">{t('detail.name_en_label')}</Label>
                    <Input
                      id="edit-name-en"
                      value={nameEn}
                      onChange={(e) => setNameEn(e.target.value)}
                      readOnly={!canWriteConfiguration}
                      data-testid="edit-name-en"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-name-fr">{t('detail.name_fr_label')}</Label>
                    <Input
                      id="edit-name-fr"
                      value={nameFr}
                      onChange={(e) => setNameFr(e.target.value)}
                      readOnly={!canWriteConfiguration}
                      data-testid="edit-name-fr"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-description">{t('detail.description_label')}</Label>
                  <Textarea
                    id="edit-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    readOnly={!canWriteConfiguration}
                    data-testid="edit-description"
                  />
                </div>
              </div>
            </div>

            {/* Configuration */}
            <div className="bg-card rounded-lg border border-border p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{t('detail.configuration_section')}</h3>
              <div className="space-y-4">
                <div>
                  <Label>{t('detail.function_label')}</Label>
                  <Select value={fn} onValueChange={(v) => setFn(v as AggregationFunctionCode)} disabled={!canWriteConfiguration}>
                    <SelectTrigger data-testid="edit-function">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FUNCTIONS.map(f => (
                        <SelectItem key={f.value} value={f.value}>
                          {t(`functions.${f.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('detail.target_asset_type_label')}</Label>
                  <Select value={targetAssetTypeCode} onValueChange={setTargetAssetTypeCode} disabled={!canWriteConfiguration}>
                    <SelectTrigger data-testid="edit-target-asset-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((at: any) => (
                        <SelectItem key={at.uid} value={at.code}>
                          <span className="flex items-center gap-2">
                            <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                            <span>{typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-target-property">{t('detail.target_property_label')}</Label>
                  <Input
                    id="edit-target-property"
                    value={targetPropertyCode}
                    onChange={(e) => setTargetPropertyCode(e.target.value)}
                    className="font-mono"
                    readOnly={!canWriteConfiguration}
                    data-testid="edit-target-property"
                  />
                </div>
                <div>
                  <Label>{t('detail.child_asset_type_label')}</Label>
                  <Select value={childAssetTypeCode} onValueChange={setChildAssetTypeCode} disabled={!canWriteConfiguration}>
                    <SelectTrigger data-testid="edit-child-asset-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((at: any) => (
                        <SelectItem key={at.uid} value={at.code}>
                          <span className="flex items-center gap-2">
                            <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                            <span>{typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedFn?.needsSource && (
                  <div>
                    <Label htmlFor="edit-source-property">{t('detail.source_property_label')}</Label>
                    <Input
                      id="edit-source-property"
                      value={sourcePropertyCode}
                      onChange={(e) => setSourcePropertyCode(e.target.value)}
                      className="font-mono"
                      readOnly={!canWriteConfiguration}
                      data-testid="edit-source-property"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('detail.source_property_help')}</p>
                  </div>
                )}

                {selectedFn?.needsFilter && (
                  <AggregationConditionEditor
                    org={org}
                    label={t('detail.filter_conditions_label')}
                    helpText={t('detail.filter_conditions_help')}
                    value={filterCondition}
                    onChange={setFilterCondition}
                    data-testid="edit-filter-conditions"
                  />
                )}

                {fn === 'ratio' && (
                  <AggregationConditionEditor
                    org={org}
                    label={t('detail.ratio_denominator_label')}
                    helpText={t('detail.ratio_denominator_help')}
                    value={ratioDenominatorCondition}
                    onChange={setRatioDenominatorCondition}
                    data-testid="edit-ratio-denominator"
                  />
                )}
              </div>
            </div>

            {/* Meta + Save */}
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground space-x-3">
                <span>UID: {agg.uid}</span>
                <span>Created: {agg.created_at ? new Date(agg.created_at).toLocaleDateString() : '-'}</span>
                <span>Updated: {agg.updated_at ? new Date(agg.updated_at).toLocaleDateString() : '-'}</span>
              </div>
              <div className="flex flex-col items-end gap-1">
                {!formValid && (
                  <p className="text-xs text-destructive" data-testid="aggregation-condition-error">
                    {t('detail.condition_incomplete_error')}
                  </p>
                )}
                {canWriteConfiguration && (
                  <Button
                    onClick={handleSave}
                    disabled={updateMutation.isPending || !formValid}
                    data-testid="save-aggregation-btn"
                  >
                    {updateMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : <AppIcon path={mdiContentSave} size="sm" className="mr-2" />}
                    {t('detail.save_button')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="test">
          <AggregationTestPanel org={org} uid={uid} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.delete_dialog.description', { name: agg.name?.en || agg.code })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">{t('detail.delete_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              {t('detail.delete_dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
